/**
 * BENCHMARK-CALC-PARAMS.MODAL
 * Configure calculation thresholds before launching benchmark questions recalculation
 */

import { useState } from "react"
import Modal from "@/components/modal"
import Card from "@/components/card"
import ListFields from "@/components/list-fields"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import Range from "@/components/range"
import Button from "@/components/button"
import BenchmarkInfoModal from "@/features/benchmark/entities/question/ui/modals/BenchmarkInfoModal"
import { faCircleInfo, faRotate } from "@fortawesome/free-solid-svg-icons"
import { CALC_QUESTION_PARAMS_DEFAULT, CalcQuestionParams } from "@/features/benchmark/entities/question/model/benchmarkQuestion.types"

interface Props {
  onClose: () => void
  onConfirm: (params: CalcQuestionParams) => void
}

export default function BenchmarkCalcParamsModal({ onClose, onConfirm }: Props) {
  const [params, setParams] = useState<CalcQuestionParams>(CALC_QUESTION_PARAMS_DEFAULT)
  const [showInfo, setShowInfo] = useState(false)

  function handleLaunch() {
    onConfirm(params)
    onClose()
  }

  return (
    <Modal
      isFullScreen
      onClose={onClose}
      title="Paramètres de calcul"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button isFullWidth onClick={() => setShowInfo(true)} isWithBorder icon={faCircleInfo}>
            Comment ça marche ?
          </Button>
          <div className="flex gap-2">
            <Button onClick={onClose} isWithBorder>
              Annuler
            </Button>
            <Button onClick={handleLaunch} className="primary" icon={faRotate}>
              Lancer le calcul
            </Button>
          </div>
        </div>
      }>
      <div>
        <p className="text-sm grey-t mb-1">
          Le calcul repart de l&apos;archive complète et reconstruit toutes les lignes benchmark questions.
        </p>
        <div className="flex gap-x-5 text-sm grey-t mb-4">
          <span>
            <span className="font-medium">1.</span> Calculer (regroupement par bank_id)
          </span>
          <span>
            <span className="font-medium">2.</span> Relancer si besoin pour rafraîchir les valeurs
          </span>
        </div>

        <Card isWithoutPadding isNotCardClass>
          <ListFields>
            <ListCell width={360} text="Seuils minimum" />
            <ListCell width={300} />
          </ListFields>

          <ListItem className="items-center py-3">
            <ListCell width={360}>
              <div className="flex flex-col gap-0.5">
                <div className="text-sm">Réponses par entreprise</div>
                <div className="text-sm grey-t">Par question, en archive</div>
              </div>
            </ListCell>
            <ListCell width={300}>
              <Range
                min={5}
                max={50}
                step={5}
                value={params.minResponsesPerAccount ?? 10}
                onChange={(v: number) => setParams((p) => ({ ...p, minResponsesPerAccount: v }))}
              />
            </ListCell>
          </ListItem>

          <ListItem className="items-center py-3">
            <ListCell width={360}>
              <div className="flex flex-col gap-0.5">
                <div className="text-sm">Entreprises par question</div>
                <div className="text-sm grey-t">Seuil global pour inclure la question</div>
              </div>
            </ListCell>
            <ListCell width={300}>
              <Range
                min={2}
                max={20}
                step={1}
                value={params.minAccountsGlobal ?? 5}
                onChange={(v: number) => setParams((p) => ({ ...p, minAccountsGlobal: v }))}
              />
            </ListCell>
          </ListItem>
        </Card>
      </div>

      {showInfo && <BenchmarkInfoModal params={params} onClose={() => setShowInfo(false)} />}
    </Modal>
  )
}
